/**
 * <StageOfferBanner> — le stage de vacances en vente, proposé sur l'accueil parent (molecule).
 *
 * C'est la **carte d'événement** du livre (chap. 06, « Les temps forts », vignette « Événement ») :
 * le nom du stage en intitulé, l'information qui compte en titre, une phrase, un bouton-poussoir,
 * et Tina dans le coin — elle célèbre. Une offre, pas une tâche : elle ne va pas dans « À faire ».
 *
 * **Elle ne porte pas le cadre en dégradé** : sur l'accueil il est pris par la prochaine séance,
 * et il n'y en a qu'un par écran. Elle ressort donc par son fond, **le jaune signature** (Wajih,
 * 12/10), accroché au marine comme le veut le livre — l'encre et le bouton sont marine, dans les
 * deux thèmes : un aplat de couleur ne s'inverse pas sur la nuit.
 *
 *  - **l'intitulé vient des données** — le libellé de la ligne du calendrier des offres, tel
 *    qu'écrit dans le BO (« Stage de la Toussaint 2026 ») ;
 *  - **le titre, ce sont les dates** des départs encore achetables ;
 *  - le bouton nomme l'enfant, « Inscrire Lina » — on inscrit quelqu'un à un stage, on ne le
 *    « réserve » ni ne l'« achète ». Sans prénom, « Inscrire mon enfant ».
 *
 * Pas de prix (le parcours le montre), pas de croix : la carte part seule à l'inscription ou à
 * la clôture des ventes.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Mascot } from "../../atoms/Mascot"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { Card } from "../Card"

export type StageOfferBannerProps = {
    /** « Stage de la Toussaint 2026 » — le libellé de la ligne du calendrier des offres. */
    title: string
    /** « Du 19 au 30 octobre » — déjà écrit par l'adaptateur. */
    datesLabel: string
    /** Le prénom de l'enfant que le parent inscrit. */
    childFirstname?: string
    /** Ouvre le parcours d'inscription. */
    onPress: () => void
    testID?: string
}

/** La place de Tina dans le coin : le texte s'arrête avant elle. */
const CHAMPION_SIZE = 124

export function StageOfferBanner(props: StageOfferBannerProps) {
    const { title, datesLabel, childFirstname, onPress, testID } = props

    const hasFirstname = childFirstname != null && childFirstname !== ""

    return (
        <Card background="accent" p="5" style={styles.card} testID={testID}>
            <Stack gap="3" style={styles.text}>
                <SectionLabel color="text-on-accent">{title}</SectionLabel>
                <Title size="text-2xl" color="text-on-accent">
                    {datesLabel}
                </Title>
                <Text size="text-md" color="text-on-accent">
                    En petit groupe pendant les vacances.
                </Text>
                <View style={styles.action}>
                    <Button
                        label={hasFirstname ? `Inscrire ${childFirstname}` : "Inscrire mon enfant"}
                        color="brand-fill"
                        onPress={onPress}
                    />
                </View>
            </Stack>
            <View style={styles.champion} pointerEvents="none">
                <Mascot name="tina" variant="happy" size={CHAMPION_SIZE} />
            </View>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Tina déborde du coin bas-droit, comme sur la page du livre : la carte la rogne. */
    card: { overflow: "hidden" },
    text: { paddingRight: CHAMPION_SIZE - theme.spacing["6"] },
    /** Le bouton garde sa taille : à droite, c'est la place de Tina. */
    action: { alignSelf: "flex-start", marginTop: theme.spacing["1"] },
    champion: { position: "absolute", right: -theme.spacing["2"], bottom: -theme.spacing["6"] },
}))
